import React from "react";
import { ToastContainer } from "react-toastify";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { pageTitle } from "src/shared/utils/brand";
import { useTimeZones } from "../hooks/useTimeZones";
import { useSignupForm } from "../hooks/useSignupForm";
import { useSignupHandler } from "../hooks/useSignupHandler";
import { GenericForm } from "../components/GenericForm";
import LoadingScreen from "../../../shared/components/LoadingScreen";
import { signupFormSchema } from "../schemas/signupFormSchema";

import imetaniclogo from "../../../assets/Images/imetanic-logo.png";
import imetanicnaming from "../../../assets/Images/imetanic-nameing-logo.png";

const Signup: React.FC = () => {
  useDocumentTitle(pageTitle("Signup"));

  const { formData, errors, inputValue, updateField, handleEmailChange, handleDobChange, handlePhoneChange, setError, clearError } = useSignupForm();

  const { timeZones, loading: timeZonesLoading } = useTimeZones();

  const { handleRegister, loading, isFormValid } = useSignupHandler({
    formData,
    errors,
    setError,
    clearError,
  });

  const hasErrors = Object.values(errors).some((error) => Boolean(error));
  const isButtonDisabled = !isFormValid || loading || hasErrors;

  return (
    <div className="h-screen flex bg-gray-100 overflow-hidden">
      <ToastContainer />

      {/* LEFT SIDE - FORM */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg flex flex-col h-full max-h-[95vh]">
          <div className="p-6 pb-4">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Get Started</h1>
            <p className="text-gray-500 mt-2 text-sm">Create your account to continue</p>
          </div>
          {/* Scrollable Form */}
          <div className="flex-1 overflow-y-auto px-6">
            <GenericForm
              schema={signupFormSchema}
              formData={formData}
              errors={errors}
              updateField={updateField}
              clearError={clearError}
              handleEmailChange={handleEmailChange}
              handleDobChange={handleDobChange}
              handlePhoneChange={handlePhoneChange}
              timeZones={timeZones}
              timeZonesLoading={timeZonesLoading}
              inputValue={inputValue}
            />
          </div>

          {/* Fixed Button */}
          <div className="p-6 pt-2">
            <button
              onClick={handleRegister}
              disabled={isButtonDisabled}
              className={`w-full py-3 rounded-xl font-semibold transition 
              ${isButtonDisabled ? "bg-blue-200 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
            >
              {loading ? "Signing up..." : "Signup"}
            </button>

            {errors.general && <p className="text-red-500 text-center mt-3">{errors.general}</p>}
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - DESIGN PANEL */}
      <div className="hidden lg:flex w-1/2 bg-blue-600 items-center justify-center text-white rounded-l-[80px] h-screen">
        <div className="text-center px-10 flex flex-col items-center justify-center">
          {/* Logo */}
          <img src={imetaniclogo} alt="iMetanic Logo" className="w-40 mb-4" />

          {/* Name Image */}
          <img src={imetanicnaming} alt="iMetanic Name" className="w-56 mb-6" />

          {/* Text */}
          <h2 className="text-3xl font-bold mb-3">Welcome to iMetanic</h2>

          <p className="text-md opacity-90 max-w-md">Manage your business smarter and faster.</p>
        </div>
      </div>

      {loading && <LoadingScreen />}
    </div>
  );
};

export default Signup;
